import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import NoLists from "assets/noLists.svg";
import { setFiltersApplied } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import Feedback from "components/common/Feedback";
import Scroller from "components/common/Scroller/InfiniteScroller";
import Spinner from "components/common/Spinner/Spinner";
import Typography from "components/common/Typography";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import colors from "styles/Colors";
import { tokens } from "styles/Tokens";
import { ENTER_KEY_CODE, SME_PAGE_SIZE, messages, minSearchStrLength } from "utils/CommonConstants";
import { addDynamicId } from "utils/CommonFunctions";
import AddInncodeDialog from "./addInncodeDialog";
import ImportDialog from "./importDialog";
import ImportUpdateDialog from "./importUpdateDialog";
import InnCodeTab from "./innCodeTab";
import MyListItem from "./myListItem";
import PlanTab from "./planTab";
import {
  fetchMyListDetails,
  fetchMyLists,
  setSMEFilters,
  setShowAddInncodeDialog,
  setShowImportDialog,
  setShowImportFailDialog,
  uploadMyList
} from "./sme.slice";

const TabItem = (props: any) => {
  return (
    <Typography
      variant="body1"
      showBold
      color={props?.isActive ? tokens.Text.General.Color : tokens.Text.General.Secondary}
      sx={{
        "&:first-of-type": {
          marginRight: "40px"
        },
        padding: "10px 0",
        borderBottom: props?.isActive ? `2px solid ${tokens.Border.General.Color}` : "",
        cursor: props?.disabled ? "not-allowed" : "pointer"
      }}
      onClick={props?.onTabChange}
    >
      {props?.label}
    </Typography>
  );
};

const SMEHome = () => {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(0);
  const { isMobileScreen } = useContext(ThemeContext);
  let [searchParams, setSearchParams] = useSearchParams();
  const [listInputValue, setListInputValue] = useState("");
  const [uploadFileDetails, setUploadFileDetails] = useState({});
  let selectedTabId = searchParams.get("tab");
  let selectedListId = searchParams.get("id");
  const [invalidInnCodes, setInvalidInncodes] = useState([]);
  const { myLists, myListDetails, status, filters } = useAppSelector((state) => state.sme);
  const { records, count } = myLists;

  useEffect(() => {
    dispatch(fetchMyLists({ size: SME_PAGE_SIZE }));
    if (selectedListId) {
      dispatch(
        fetchMyListDetails({
          id: selectedListId,
          view: selectedTabId ?? "inncodes",
          engagement: filters?.engagement.join(","),
          innCode: filters?.innCode.join(","),
          size: SME_PAGE_SIZE
        })
      );
    }
  }, []);

  useEffect(() => {
    if (listInputValue) {
      if (count) {
        // @ts-ignore
        setSearchParams({ id: records[0]?.id, tab: "inncodes" });
      }
    } else if (selectedListId) {
      if (count) setSearchParams({ id: selectedListId, tab: selectedTabId || "inncodes" });
    }
    // @ts-ignore
    else if (records[0]?.id) {
      // @ts-ignore
      setSearchParams({ id: records[0]?.id, tab: selectedTabId || "inncodes" });
    }
  }, [myLists, listInputValue]);

  const fetchMore = () => {
    if (status === "loading") {
      return;
    }

    dispatch(fetchMyLists({ page: page + 1, searchText: listInputValue, size: SME_PAGE_SIZE })).then((res: any) => {
      dispatch(
        fetchMyListDetails({
          id: selectedListId,
          view: selectedTabId ?? "inncodes",
          engagement: filters?.engagement.join(","),
          innCode: filters?.innCode.join(","),
          size: SME_PAGE_SIZE
        })
      );
    });
    setPage((pageNo) => pageNo + 1);
  };

  const handleSubmitImport = (data: any) => {
    const formData = new FormData();

    formData.append("file", data?.importFile[0]);
    formData.append("name", data?.listName);

    dispatch(uploadMyList(formData)).then((response: any) => {
      if (response?.payload?.id) {
        searchParams.set("id", response?.payload?.id);
        searchParams.set("tab", selectedTabId ?? "inncodes");
        setSearchParams(searchParams);
      }

      if (response?.payload?.invalidMappings?.length) {
        dispatch(setShowImportFailDialog(true));
        setUploadFileDetails({ id: response?.payload?.id, name: response?.payload?.name });
        const newRes = addDynamicId(response?.payload?.invalidMappings);
        setInvalidInncodes(newRes);
      } else {
        setShowImportDialog(false);
      }
    });
  };

  const handleSelectList = (data: any) => {
    if (selectedListId !== data?.id.toString()) {
      setSearchParams({ id: data?.id, tab: "inncodes" });
      dispatch(setFiltersApplied(false));
      dispatch(
        setSMEFilters({
          innCode: [],
          engagement: [],
          statsEngagement: [],
          planStatus: []
        })
      );
    }
  };

  useEffect(() => {
    if (selectedListId) {
      dispatch(
        fetchMyListDetails({
          id: selectedListId,
          view: selectedTabId ?? "inncodes",
          engagement: filters?.engagement.join(","),
          innCode: filters?.innCode.join(","),
          size: SME_PAGE_SIZE
        })
      );
    } else {
      searchParams.delete("id");
      searchParams.delete("tab");
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  const handleTabChange = (tabName: string) => {
    dispatch(setFiltersApplied(false));
    dispatch(
      setSMEFilters({
        innCode: [],
        engagement: [],
        statsEngagement: [],
        planStatus: []
      })
    );
    searchParams.set("tab", tabName);
    setSearchParams(searchParams);
  };

  const freshSearch = () => {
    const props = {
      searchText: "",
      page: 0,
      size: SME_PAGE_SIZE
    };

    dispatch(fetchMyLists(props)).then((res: any) => {
      if (res?.payload?.innCodeGroups[0]?.id) {
        setSearchParams({ id: res?.payload?.innCodeGroups[0]?.id, tab: "inncodes" });
        dispatch(
          fetchMyListDetails({
            id: res?.payload?.innCodeGroups[0]?.id,
            view: "inncodes",
            size: SME_PAGE_SIZE
          })
        );
      }
    });
  };

  const onSearch = (e: any) => {
    const { value } = e.target;

    if (e.charCode === ENTER_KEY_CODE || e.key === "Enter") {
      if (value.trim() && value.trim().length > minSearchStrLength) {
        dispatch(fetchMyLists({ page: 0, searchText: value, size: SME_PAGE_SIZE }));
      }

      if (!value.trim()) {
        freshSearch();
      }
    }
  };

  const handleListInputChange = (e: any) => {
    const { value } = e.target;

    setListInputValue(value);

    if (!value.trim()) {
      freshSearch();
    }
  };

  return (
    <>
      <Feedback form="smeDeleteMyList" message={messages.listDeleteSuccess} />
      <Feedback form="smeDeleteMapping" message={messages.inncodeDeleteSuccess} />
      <Feedback form="smeMapUserToInncode" message={messages.inncodeAddSuccess} />
      <Feedback form="smeUploadMyList" message={messages.listUploadSuccess} />
      <Stack
        spacing={2}
        direction={isMobileScreen ? "column" : "row"}
        alignItems="center"
        sx={{
          paddingY: "24px",
          paddingRight: isMobileScreen ? "0" : "32px",
          borderBottom: `1px solid ${tokens.Border.General.Tertiary}`
        }}
      >
        <Typography
          variant="h4"
          showBold
          color={tokens.Text.General.PrimaryHeadline}
          sx={{
            flex: 1,
            textAlign: isMobileScreen ? "center" : "left"
          }}
        >
          My Dashboard
        </Typography>
        {records.length ? (
          <>
            <Button
              variant="secondary"
              label="Import Inncode List"
              onClick={() => dispatch(setShowImportDialog(true))}
              sx={{ width: isMobileScreen ? "100%" : "auto" }}
            />
            <Button
              variant="secondary"
              label="Add Inncode"
              onClick={() => dispatch(setShowAddInncodeDialog(true))}
              sx={{ width: isMobileScreen ? "100%" : "auto" }}
            />
          </>
        ) : null}
      </Stack>
      {records.length || listInputValue ? (
        <>
          <Stack direction="row">
            <Box
              sx={{
                width: isMobileScreen ? "100%" : "300px",
                borderRight: isMobileScreen ? "none" : `1px solid ${tokens.Border.General.Tertiary}`,
                paddingY: "16px"
              }}
            >
              <Typography variant="body1" showBold color={tokens.Text.General.PrimaryBody} sx={{ marginBottom: "8px" }}>
                My List
              </Typography>
              <TextField
                onKeyUp={onSearch}
                value={listInputValue}
                onChange={handleListInputChange}
                placeholder="Search by list"
                sx={{ height: "34px", maxWidth: isMobileScreen ? "100%" : "276px" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: listInputValue.length ? (
                    <InputAdornment position="end">
                      <CloseIcon
                        sx={{ cursor: "pointer", fontSize: "20px" }}
                        onClick={() => {
                          setListInputValue("");
                          freshSearch();
                          setPage(0);
                        }}
                      />
                    </InputAdornment>
                  ) : null
                }}
              />
            </Box>

            {!isMobileScreen && myListDetails?.name && (
              <Stack
                sx={{
                  flex: 1,
                  padding: "0 24px",
                  justifyContent: "center",
                  backgroundColor: tokens.Background.General.Secondary,
                  borderBottom: `1px solid ${tokens.Border.General.Tertiary}`
                }}
              >
                <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline}>
                  {myListDetails?.name}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Stack direction="row">
            <Box
              id="listsDiv"
              sx={{
                minWidth: isMobileScreen ? "100%" : "300px",
                borderRight: `1px solid ${tokens.Border.General.Tertiary}`,
                paddingRight: "24px",
                height: isMobileScreen ? "200px" : "calc(100vh - 280px)",
                overflowY: "auto"
              }}
            >
              <Scroller
                dataLength={records.length}
                loadMore={fetchMore}
                totalList={count}
                label=""
                scrollableTarget="listsDiv"
              >
                {records.map((list: any) => (
                  <MyListItem key={list.id} data={list} onSelect={handleSelectList} selected={selectedListId} />
                ))}
              </Scroller>
            </Box>
            {!isMobileScreen && (
              <Stack sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" sx={{ paddingX: "24px" }}>
                  <TabItem
                    label="Inncodes"
                    onTabChange={() => handleTabChange("inncodes")}
                    isActive={selectedTabId === "inncodes"}
                  />
                  <TabItem
                    label="Plans"
                    onTabChange={myListDetails?.assignedPlanCount ? () => handleTabChange("plans") : () => null}
                    isActive={selectedTabId === "plans"}
                    disabled={myListDetails?.assignedPlanCount < 1}
                  />
                </Stack>
                <Box sx={{ height: isMobileScreen ? "200px" : "calc(100vh - 324px)", overflowY: "auto" }} id="tableDiv">
                  {selectedTabId === "inncodes" ? <InnCodeTab selectedListId={selectedListId} /> : null}
                  {selectedTabId === "plans" ? <PlanTab selectedListId={selectedListId} /> : null}
                </Box>
              </Stack>
            )}
          </Stack>
          {isMobileScreen && (
            <Stack sx={{ marginTop: "32px" }}>
              <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline}>
                {myListDetails?.name}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                sx={{ marginTop: "32px", borderBottom: `2px solid ${tokens.Border.General.Tertiary}` }}
              >
                <TabItem
                  label="Inncodes"
                  onTabChange={() => handleTabChange("inncodes")}
                  isActive={selectedTabId === "inncodes"}
                />
                <TabItem
                  label="Plans"
                  onTabChange={myListDetails?.assignedPlanCount ? () => handleTabChange("plans") : () => null}
                  isActive={selectedTabId === "plans"}
                  disabled={myListDetails?.assignedPlanCount < 1}
                />
              </Stack>
              <Box sx={{ backgroundColor: tokens.Background.General.Primary }}>
                {selectedTabId === "inncodes" ? <InnCodeTab selectedListId={selectedListId} /> : null}
                {selectedTabId === "plans" ? <PlanTab selectedListId={selectedListId} /> : null}
              </Box>
            </Stack>
          )}
        </>
      ) : (
        status !== "loading" && (
          <Box
            sx={{
              marginRight: isMobileScreen ? 0 : "32px",
              marginY: "32px",
              borderRadius: "8px",
              backgroundColor: colors.Blue.Blue300,
              position: "relative",
              padding: isMobileScreen ? "65px 20px" : "68px 48px",
              backgroundImage: `url(${NoLists})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: isMobileScreen ? "cover" : "50%",
              backgroundPosition: isMobileScreen ? "center" : "right center",
              textAlign: isMobileScreen ? "center" : "left",
              overflow: "hidden"
            }}
          >
            {isMobileScreen && (
              <Box
                sx={{
                  top: 0,
                  bottom: 0,
                  right: 0,
                  left: 0,
                  position: "absolute",
                  height: "inherit",
                  background: "linear-gradient(0deg, rgba(41, 148, 255, 0.90) 53.13%, rgba(41, 148, 255, 0.20) 100%)"
                }}
              />
            )}
            <Typography
              variant="h4"
              showBold
              color={tokens.Text.General.Inverse}
              sx={{ zIndex: 1, position: "relative" }}
            >
              No list have been uploaded.
            </Typography>
            <Typography
              variant={isMobileScreen ? "label" : "body1"}
              color={tokens.Text.General.Inverse}
              sx={{ marginTop: "8px", display: "block", zIndex: 1, position: "relative" }}
            >
              Upload your Inncode list when you are ready.
            </Typography>
            <Button
              label="Import Inncode List"
              variant="primaryWhite"
              onClick={() => dispatch(setShowImportDialog(true))}
              sx={{ marginTop: "32px" }}
            />
          </Box>
        )
      )}
      {status === "loading" && <Spinner backdropVisible />}
      <ImportDialog onSubmit={handleSubmitImport} />
      <ImportUpdateDialog uploadFileDetails={uploadFileDetails} invalidInncodes={invalidInnCodes} />
      <AddInncodeDialog listId={selectedListId} />
    </>
  );
};

export default SMEHome;
